import useAppQuery, { useAppQueryMap } from 'hooks/use-app-query'
import { TeamListItem, Teams, TeamsPagination } from 'modules/Settings/Teams/types'
import { getTeam, getTeamMemberLookup, getTeams } from 'services/team'
import {
  GetTeamResponse,
  GetTeamsPayload,
  GetTeamsResponse,
  TeamListItem as TeamListItemService,
} from 'services/types/team'
import { Team } from 'types/global'
import { teamKey } from 'utils/constants/queryKey'

const sortKeys: { [key in keyof TeamListItem]: keyof TeamListItemService } = {
  createTs: 'create_ts',
  memberCount: 'member_count',
  parentId: 'parent_id',
  parentName: 'parent_name',
  teamId: 'team_id',
  teamName: 'team_name',
}

const prepareGetTeamsPayload = (pagination: TeamsPagination, searchValue?: string): GetTeamsPayload => ({
  page: pagination.page,
  per_page: pagination.perPage,
  sort_by: {
    key: sortKeys[pagination.sortBy.key],
    order: pagination.sortBy.order,
  },
  ...(searchValue ? { query: searchValue } : {}),
})

const mapNestedTeam = (team: GetTeamResponse['data']['subteams']): Team['subTeams'] =>
  team?.map((i) => ({
    child: i.child ? mapNestedTeam(i.child) : null,
    id: i.id,
    name: i.name,
  })) || null

const mapTeam = (responseData: GetTeamResponse): Team => ({
  description: responseData.data.description,
  members: responseData.data.team_members.map((i) => ({
    branch: i.branch,
    department: i.department,
    email: i.email,
    fname: i.fname,
    jobTitle: i.job_title,
    lname: i.lname,
    location: i.location,
    photoUrl: i.photo_url,
    status: i.is_active ? 'Active' : 'Deactivated',
    teams: [], // Can keep this empty for now; not needed in Team Members table
    userId: i.user_id,
    userProfileId: i.user_profile_id,
    userProfileName: i.user_profile_name,
  })),
  parentId: responseData.data.parent_id,
  parentName: responseData.data.parent_name,
  subTeams: mapNestedTeam(responseData.data.subteams),
  teamId: responseData.data.team_id,
  teamName: responseData.data.team_name,
})

const mapTeams = (responseData: GetTeamsResponse): Teams => ({
  data:
    responseData.data?.map((i) => ({
      createTs: i.create_ts,
      memberCount: i.member_count,
      parentId: i.parent_id,
      parentName: i.parent_name,
      teamId: i.team_id,
      teamName: i.team_name,
    })) || [],
  totalCount: responseData.total_count,
})

export const useTeams = (pagination: TeamsPagination, searchValue?: string) => {
  const payload = prepareGetTeamsPayload(pagination, searchValue)
  return useAppQueryMap(teamKey.list(payload), () => getTeams(payload), mapTeams, { keepPreviousData: true })
}

export const useTeamDetail = (teamId: string | null) =>
  useAppQueryMap(teamKey.detail(teamId ?? undefined), () => getTeam({ team_id: teamId }), mapTeam, {
    enabled: !!teamId,
  })

export const useTeamMemberLookup = (teamId: string | null) =>
  useAppQuery(teamKey.memberLookup(teamId), () => getTeamMemberLookup({ team_id: teamId }), {
    enabled: !!teamId,
  })
